<template>
  <div>
    <v-divider />
    <v-card-text
      v-if="campaign.storefront_pricing"
      class="text-right"
    >
      <!-- Every figure below comes from `figures`: the SERVER's breakdown
           (`checkout_quote.totals`) when the cart has one for its current
           inputs, the cart's own arithmetic otherwise -- and then the
           Subtotal says "(estimated)". Bug Intake #481: one set of numbers
           on the cart, the confirmation, the email and the order page; see
           cartTotalsDisplay.js. -->
      <!-- Items -->
      <div data-testid="cart-items">Items: ${{ figures.items.toFixed(2) }}</div>
      <!-- Tax.
           "TBD" used to render here in both presentations. It reads to a
           shopper as "we could not work this out"; it actually meant "not
           calculated yet". `salesTaxState` replaces it with copy that says
           which of four things is true -- see the prop's comment below. -->
      <span>
        <span v-if="consolidateTaxFee" data-testid="cart-tax-and-fee">
          <div
            v-if="!figures.fromServer && salesTaxState === 'calculating'"
            class="text-grey"
          >
            Calculating Sales Tax
          </div>
          <!-- Rendered whenever the store consolidates AND the line has a
               non-zero figure to show.

               Two conjuncts were REMOVED here on 2026-09-16, because a
               deployed smoke test of #3562 on sandbox 8087 found this row
               blank in the store's DEFAULT state: the wrapper rendered and
               every branch inside it was false, leaving Items 19.92 above a
               Subtotal of 21.59 with nothing explaining 1.67.

               `!shipToShopperAddress` -- arrived pre-formed in the 110-file
               bulk refactor #433 with no rationale attached to this row, and
               `shipToShopperAddress` appears NOWHERE else in this component.
               Shipping to the shopper says nothing about the fees: they are
               charged either way. The sibling non-consolidated branch below
               never had this gate; it renders the row and lets
               `salesTaxState` supply the copy. State belongs in the copy,
               not in whether a money row exists.

               `collectSalesTax` -- this is no longer a TAX line. Since #3562
               it carries tax + processing + (when the shopper bears it) the
               platform fee, and the last two exist whether or not the store
               collects tax. `consolidate_tax_fee` and `collect_sales_tax`
               are independent columns with no coupling in the model or the
               API, so "consolidates, does not collect tax" is reachable --
               and there the old gate hid a row carrying real money.

               The `> 0` conjunct STAYS: a zero line is noise, not honesty. -->
          <div
            v-else-if="figures.taxAndFees > 0"
          >
            <!-- "Tax and Fees:", Jake's wording (2026-09-15). Was "Sales Tax
                 & Processing Fee", which named exactly two components and so
                 could not honestly cover the platform fee now summed into
                 this line when the shopper bears it. -->
            Tax and Fees:
            <span
              v-if="
                !figures.fromServer &&
                  (salesTaxState === 'not-calculated' || isCartProcessingFeeTBD)
              "
              >Calculated at checkout</span
            >
            <!-- `cartProcessingFee` ALONE, not `.add(appFee)`. `appFee` is
                 the legacy 3.95% that duplicates the canonical platform fee
                 and is NO LONGER part of the charge (order_money.py /
                 StorefrontCart cartCharge). Summing it here would show a fee
                 larger than the one the card is charged -- see the
                 standalone row below and
                 __tests__/cart-display-fee-equals-charged-fee.spec.js.

                 `consolidatedTaxAndFees` adds the PLATFORM fee on top, and
                 only when the shopper bears it. That is what makes this one
                 line foot to the Total; see the computed. -->
            <span v-else>${{ figures.taxAndFees.toFixed(2) }}</span>
          </div>
        </span>
        <span v-else data-testid="cart-tax">
          <div
            v-if="!figures.fromServer && salesTaxState === 'calculating'"
            class="text-grey"
          >
            Calculating Sales Tax
          </div>
          <div v-else-if="collectSalesTax">
            Sales Tax:
            <span
              v-if="!figures.fromServer && salesTaxState === 'not-calculated'"
              >Calculated at checkout</span
            ><span
              v-else-if="salesTaxState === 'not-collected' && figures.salesTax === 0"
              >N/A</span
            ><span v-else>${{ figures.salesTax.toFixed(2) }}</span>
          </div>
        </span>
      </span>
      <!-- Coupon -->
      <div v-if="couponAmount > 0">
        <v-tooltip location="left">
          <template v-slot:activator="{ props }">
            <v-btn
              icon
              color="white"
              v-bind="props"
              class="icon-info"
            >
              <v-icon icon="info" />
            </v-btn>
          </template>
          <span>
            You receive -{{
              couponType === "$"
                ? couponType + parseFloat(couponAmount).toFixed(2)
                : ""
            }}
            {{ couponType === "%" ? couponAmount + couponType : "" }}
            off your order<br />
            from the coupon {{ coupon }}
          </span>
        </v-tooltip>
        <!-- The server's dollar effect once quoted: a "15%" alone does not
             say what came off this cart. -->
        <span v-if="figures.fromServer"
          >Coupon: -${{ figures.couponDiscount.toFixed(2) }}</span
        >
        <span v-else
          >Coupon: -{{
            couponType === "$"
              ? couponType + parseFloat(couponAmount).toFixed(2)
              : ""
          }}
          {{ couponType === "%" ? couponAmount + couponType : "" }}</span
        >
      </div>
      <!-- Highest-priced item free (https://trello.com/c/T756dpsG).
           Shown as its own line so a guest can SEE why the total dropped;
           an unexplained discount at the till reads as a pricing bug. -->
      <div
        v-if="figures.freeItem > 0"
        data-testid="cart-free-item-discount"
      >
        Free item: -${{ figures.freeItem.toFixed(2) }}
      </div>
      <!-- Shipping -->
      <div
        v-if="
          campaign.shipping_cost_enabled &&
            calculatingShipping &&
            !figures.fromServer
        "
        class="text-grey"
      >
        Calculating Shipping Cost
      </div>
      <div
        v-else-if="campaign.shipping_cost_enabled"
        data-testid="cart-summary-shipping-cost"
      >
        Shipping Cost:
        <span v-if="isShippingCostTBD && !figures.fromServer">TBD</span
        ><span v-else>${{ figures.shipping.toFixed(2) }}</span>
      </div>
      <!-- Processing Fee.
           Rendered ABOVE the subtotal, because the subtotal INCLUDES it
           (Jake, 2026-09-14). Tax, coupon and shipping were already folded
           in; the fee was the one component shown below the line, which
           reads as a charge appended after the total was struck. -->
      <div
        v-if="
          !consolidateTaxFee &&
            (figures.fromServer ? figures.processingFee > 0 : collectProcessingFee)
        "
        data-testid="cart-processing-fee"
      >
        <!-- THE DISPLAYED FEE MUST EQUAL THE CHARGED FEE. This was
             `cartProcessingFee.add(appFee)`, correct only while `appFee` was
             added to the charge. It is the legacy 3.95% duplicating the
             canonical platform fee and is no longer charged, so re-summing
             it here would show the shopper ~3.95% of the cart more than
             their card is taking -- a displayed-vs-charged mismatch that no
             checkout 409 can catch.

             `.format()` rather than `${{ ....value }}`: currency.js `.value`
             is a raw Number, so a $3.20 fee renders "$3.2". The old
             expression hid that because `.add()` returns a currency object.
             Dropping the sum must not silently drop the cent formatting on a
             money row -- `.format()` supplies both the `$` and the cents. -->
        Processing Fee:
        <span v-if="isCartProcessingFeeTBD && !figures.fromServer">TBD</span
        ><span v-else>${{ figures.processingFee.toFixed(2) }}</span>
      </div>
      <!-- Service Fee -- the ThreadFlow platform fee, as its OWN line
           (Jake, 2026-09-15).
           Shown ONLY when consolidation is off AND the shopper bears the
           fee. A 2026-07-23 decision had baked it into the total with no
           line at all; Jake overrode that because the screen did not foot.
           Jake drove order AT-2026-01033 and saw Items 17.02 + Processing
           0.79 = Subtotal 17.81 above a Total of 18.48, with nothing on
           screen accounting for 67c.
           Rendered ABOVE the subtotal for the same reason the processing fee
           is: the subtotal INCLUDES it (utils/cartSubTotal.js).
           When the DISTRIBUTOR absorbs the fee there is no row, because the
           shopper is not charged it -- showing it would both overstate their
           total and leak ThreadFlow's margin onto a customer's screen. -->
      <div
        v-if="!consolidateTaxFee && figures.serviceFee > 0"
        data-testid="cart-service-fee"
      >
        Service Fee: ${{ figures.serviceFee.toFixed(2) }}
      </div>
      <!-- Stripe's $0.50 minimum on a tiny card order: the server shows what
           the clamp added as its own line, so the column still foots. -->
      <div
        v-if="figures.minimumCharge > 0"
        data-testid="cart-minimum-charge"
      >
        Minimum charge: ${{ figures.minimumCharge.toFixed(2) }}
      </div>
      <!-- Subtotal. The JS figure is only an estimate until the server
           answers for this cart, and says so. -->
      <div data-testid="cart-subtotal">
        Subtotal: ${{ figures.subtotal > 0 ? figures.subtotal.toFixed(2) : "0.00" }}
        <span
          v-if="!figures.fromServer"
          class="text-medium-emphasis"
          data-testid="cart-subtotal-estimate"
        >(estimated)</span>
      </div>
      <!-- Allowance -->
      <div
        v-if="figures.allowance > 0"
        data-testid="cart-company-allowance"
      >
        Allowance: -${{ figures.allowance.toFixed(2) }}
      </div>
    </v-card-text>
    <v-divider class="mt-2 mb-2" />
    <v-card-actions v-if="campaign.storefront_pricing" class="mt-1">
      <v-spacer></v-spacer>
      <v-tooltip location="left" v-if="showMinChargeText">
        <template v-slot:activator="{ props }">
          <v-btn icon variant="plain" v-bind="props">
            <v-icon icon="info" />
          </v-btn>
        </template>
        <span> A minimum charge of 50 cent is required </span>
      </v-tooltip>
    </v-card-actions>
  </div>
</template>

<script>
import currency from "currency.js";

import { cartDisplayFigures } from "./cartTotalsDisplay";

export default {
  name: "CartSummary",

  props: {
    campaign: {
      type: Object,
      required: true,
    },
    total: {
      type: Number,
      required: true,
    },
    subTotal: {
      type: Number,
      required: true,
    },
    cartCharge: {
      type: Number,
      required: true,
    },
    computedSalesTax: {
      type: Number,
      default: 0,
    },
    collectSalesTax: {
      type: Boolean,
      default: false,
    },
    consolidateTaxFee: {
      type: Boolean,
      default: false,
    },
    collectProcessingFee: {
      type: Boolean,
      default: false,
    },
    couponAmount: {
      type: Number,
      default: 0,
    },
    couponType: {
      type: String,
      default: "%",
    },
    coupon: {
      type: String,
      default: "",
    },
    shippingCost: {
      type: Number,
      default: 0,
    },
    companyAllowance: {
      type: Number,
      default: 0,
    },
    // Highest-priced-item-free (https://trello.com/c/T756dpsG). Defaults to
    // 0 so every store that has not enabled the rule renders no row at all.
    freeItemDiscount: {
      type: Number,
      default: 0,
    },
    cartProcessingFee: {
      type: Object,
      required: true,
    },
    appFee: {
      type: Object,
      required: true,
    },
    isSalesTaxTBD: {
      type: Boolean,
      default: true,
    },
    /**
     * Which of four things is true about the sales tax right now.
     *
     *   "not-calculated" -- nothing looked up yet -> "Calculated at checkout"
     *   "calculating"    -- lookup in flight      -> "Calculating Sales Tax"
     *   "calculated"     -- tax IS collected      -> "$x.xx", including a
     *                                                genuine "$0.00"
     *   "not-collected"  -- calculated, no tax    -> "N/A"
     *
     * Replaces a bare "TBD" that rendered for three of those four
     * indistinguishably.
     *
     * "N/A" covers every way a store collects nothing -- no nexus, the store
     * not collecting tax, a tax_exempt company location -- because each is
     * the owner's configured choice rather than something to explain
     * (Jake + Mike, 2026-09-14). It must NEVER appear before a calculation
     * has run; "not-calculated" is for that, and conflating the two
     * recreates the confusion this change exists to remove.
     *
     * "$0.00" stays distinct from "N/A" because a zero RATE inside a nexus
     * state is the common case for some stores -- every Oregon zip is
     * 0.000000, and Oregon is in the reference distributor's nexus_states.
     * Tax is collected there; the rate is simply zero.
     *
     * Defaults to "not-calculated" so a parent that has not been updated
     * shows the deferred wording rather than asserting a figure it does not
     * have -- the same direction the old `isSalesTaxTBD: true` default
     * pointed.
     */
    salesTaxState: {
      type: String,
      default: "not-calculated",
      validator: (value) =>
        [
          "not-calculated",
          "calculating",
          "calculated",
          "not-collected",
        ].includes(value),
    },
    isShippingCostTBD: {
      type: Boolean,
      default: true,
    },
    isCartProcessingFeeTBD: {
      type: Boolean,
      default: false,
    },
    salesTaxLoading: {
      type: Boolean,
      default: false,
    },
    calculatingShipping: {
      type: Boolean,
      default: false,
    },
    showMinChargeText: {
      type: Boolean,
      default: false,
    },
    shipToShopperAddress: {
      type: Boolean,
      default: false,
    },
    /**
     * The ThreadFlow platform fee for this cart, in dollars.
     *
     * Passed in as a FIGURE rather than recomputed from a rate here, for the
     * same reason the processing fee is: a second definition of what a fee
     * costs is how a displayed total and a charged total drift apart. The
     * parent derives it from the same inputs `cartCharge` uses.
     */
    platformFee: {
      type: Number,
      default: 0,
    },
    /**
     * Whether the SHOPPER bears the platform fee (the parent resolves this
     * from `stores.platform_fee_bearer`: only the exact string
     * "distributor" moves it off the shopper).
     *
     * DEFAULTS FALSE, deliberately the OPPOSITE of the backend's fail-safe.
     * `resolve_platform_fee_bearer` defaults to charging the shopper,
     * because a missing bearer must not silently move money. This is a
     * DISPLAY default, where the risk runs the other way: if this prop ever
     * goes unbound (the #3498 / #3549 defect, which hit these very
     * components), a `true` default would print ThreadFlow's cut on the
     * screen of a shopper whose distributor absorbs it. A `false` default
     * degrades to the pre-existing bug instead of inventing a new one. The
     * parent, which knows the real bearer, is what turns the row on.
     */
    shopperBearsPlatformFee: {
      type: Boolean,
      default: false,
    },
    /**
     * THE breakdown: `checkout_quote.totals` for the cart's CURRENT inputs,
     * or null while there is none (no address yet, a quote in flight, a
     * failed quote). When present every figure on this card is the server's
     * and nothing is added up here; see cartTotalsDisplay.js.
     */
    serverTotals: {
      type: Object,
      default: null,
    },
  },

  computed: {
    /** The figures this card shows (server first, the JS estimate else). */
    figures() {
      return cartDisplayFigures(this.serverTotals, {
        items: this.total,
        salesTax: this.computedSalesTax,
        taxAndFees: this.consolidatedTaxAndFees.value,
        shipping: this.shippingCost,
        freeItem: this.freeItemDiscount,
        processingFee: this.cartProcessingFee.value,
        serviceFee:
          this.shopperBearsPlatformFee && this.platformFee > 0
            ? this.platformFee
            : 0,
        subtotal: this.subTotal,
        allowance: this.companyAllowance,
        total: this.cartCharge,
      });
    },

    /**
     * The single consolidated "Tax and Fees" figure.
     *
     * Sales tax + processing fee, plus the platform fee ONLY when the
     * shopper bears it. That conditional addend is what makes the
     * consolidated presentation foot: the subtotal below includes the
     * platform fee on exactly the same condition
     * (utils/cartSubTotal.js), so a shopper adding Items to this one line
     * arrives at the Total.
     *
     * `appFee` is deliberately NOT summed -- it is the legacy 3.95% that
     * duplicates this same platform fee from a different table and is no
     * longer part of the charge.
     */
    consolidatedTaxAndFees() {
      // Re-wrapped in `currency()` rather than chaining off whatever
      // `.add()` returned. `cartProcessingFee` is a PROP, and callers --
      // including several existing specs -- supply plain objects whose
      // `.add()` returns something without `.format()`. Depending on the
      // shape of a prop's return value makes this row throw a render error
      // on a money line, which is a blank total rather than a wrong one.
      const base = currency(
        this.cartProcessingFee.add(this.computedSalesTax).value
      );
      return this.shopperBearsPlatformFee ? base.add(this.platformFee) : base;
    },
  },
};
</script>
